import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { useAuth } from './AuthContext';
import { useI18n } from '../i18n/I18nContext';
import getApiBase from '../utils/apiBase';
import './AdminUsers.css';

const API = `${getApiBase()}/api/admin/users`;
const PAGE_SIZE = 50;
const ROLES = ['customer', 'admin'];
const STATUSES = ['active', 'disabled'];
const ERROR_KEYS = {
  admin_users_invalid_query: 'admin.users.invalidQuery',
  admin_users_rate_limited: 'admin.users.rateLimited',
  admin_users_unavailable: 'admin.users.loadFailed',
  unauthorized: 'admin.users.accessDenied',
};

function readAdminToken() {
  try { return localStorage.getItem('adminAuth') || ''; } catch { return ''; }
}

function saveAdminToken(token) {
  try {
    if (token) localStorage.setItem('adminAuth', token);
    else localStorage.removeItem('adminAuth');
  } catch {
    // Administrator access still works when browser storage is unavailable.
  }
}

export default function AdminUsers() {
  const { user, loading: authLoading, logout } = useAuth();
  const { t, formatDate, formatNumber } = useI18n();
  const [auth, setAuth] = useState(readAdminToken);
  const [login, setLogin] = useState({ username: '', password: '' });
  const [sessionDenied, setSessionDenied] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({ q: '', page: 1, refresh: 0 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(null);
  const authorized = Boolean(auth || (user?.role === 'admin' && !sessionDenied));
  const accessKey = auth ? `basic:${auth}` : `session:${user?.id || ''}`;

  useEffect(() => {
    const syncCredentials = event => {
      if (event.key !== 'adminAuth' && event.key !== null) return;
      request.current?.abort();
      setData(null);
      setError('');
      setLoading(false);
      setAuth(readAdminToken());
    };
    window.addEventListener('storage', syncCredentials);
    return () => window.removeEventListener('storage', syncCredentials);
  }, []);

  useEffect(() => {
    if (authLoading || !authorized) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    request.current = controller;
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    const params = new URLSearchParams({ q: query.q, page: String(query.page), pageSize: String(PAGE_SIZE) });
    fetch(`${API}?${params}`, {
      credentials: 'include',
      headers: auth ? { Authorization: `Basic ${auth}` } : {},
      signal: controller.signal,
    }).then(async response => {
      if (!active || controller.signal.aborted) return;
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        if (!active || controller.signal.aborted) return;
        if (response.status === 401 || response.status === 403) {
          saveAdminToken('');
          setAuth('');
          setSessionDenied(true);
          setError('admin.users.accessDenied');
        } else {
          const fallback = response.status === 429 ? 'admin.users.rateLimited' : response.status === 400 ? 'admin.users.invalidQuery' : 'admin.users.loadFailed';
          setError(Object.prototype.hasOwnProperty.call(ERROR_KEYS, body?.errorCode) ? ERROR_KEYS[body.errorCode] : fallback);
        }
        return;
      }
      const body = await response.json();
      if (!active || controller.signal.aborted) return;
      if (!Array.isArray(body.items) || !Number.isSafeInteger(body.total) || body.total < 0) {
        setError('admin.users.loadFailed');
        return;
      }
      setData({ ...body, accessKey });
      if (auth) saveAdminToken(auth);
    }).catch(() => {
      if (active && !controller.signal.aborted) setError('admin.users.loadFailed');
    }).finally(() => {
      if (active && !controller.signal.aborted) setLoading(false);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [accessKey, auth, authLoading, authorized, query]);

  const changeQuery = (next) => {
    request.current?.abort();
    setData(null);
    setQuery(current => ({ ...current, ...next, refresh: current.refresh + 1 }));
  };

  const handleSearch = event => {
    event.preventDefault();
    const q = search.trim();
    if (q.length > 120) {
      setError('admin.users.invalidQuery');
      return;
    }
    changeQuery({ q, page: 1 });
  };

  const handleLogin = event => {
    event.preventDefault();
    if (login.username.includes(':')) {
      setError('admin.users.accessDenied');
      return;
    }
    try {
      const bytes = new TextEncoder().encode(`${login.username}:${login.password}`);
      const token = btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''));
      setAuth(token);
      setSessionDenied(false);
      setLogin(current => ({ ...current, password: '' }));
      changeQuery({ q: '', page: 1 });
      setSearch('');
    } catch {
      setError('admin.users.accessDenied');
    }
  };

  const handleLogout = () => {
    request.current?.abort();
    saveAdminToken('');
    setAuth('');
    setSessionDenied(true);
    setData(null);
    setSearch('');
    setQuery({ q: '', page: 1, refresh: 0 });
    setError('');
    setLoading(false);
    setLogin({ username: '', password: '' });
    if (user?.role === 'admin') logout().catch(() => {});
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const registeredAt = value => value && !Number.isNaN(new Date(value).getTime())
    ? formatDate(value, { dateStyle: 'medium', timeStyle: 'short' })
    : t('admin.notSet');

  return <main className="admin-users">
    <Seo title={t('admin.users.title')} description={t('admin.users.description')} path="/admin/users" noindex />
    <header className="admin-users-header">
      <div>
        <h1>{t('admin.users.title')}</h1>
        <p>{t('admin.users.intro')}</p>
        <nav aria-label={t('admin.users.navigation')}>
          <Link to="/admin">{t('admin.nav.products')}</Link>
          <Link to="/admin/orders">{t('admin.nav.orders')}</Link>
          <Link to="/admin/analytics">{t('analytics.nav')}</Link>
        </nav>
      </div>
      {authorized && <button type="button" onClick={handleLogout}>{t('admin.logout')}</button>}
    </header>
    {error && <p className="admin-users-error" role="alert">{t(error)}</p>}
    {authLoading ? <p role="status">{t('admin.loading')}</p> : !authorized ? <section className="admin-users-login">
      <h2>{t('admin.loginTitle')}</h2>
      <form onSubmit={handleLogin}>
        <label>{t('admin.username')}<input autoComplete="username" maxLength={100} required value={login.username} onChange={event => setLogin(current => ({ ...current, username: event.target.value }))} /></label>
        <label>{t('admin.password')}<input type="password" autoComplete="current-password" maxLength={256} required value={login.password} onChange={event => setLogin(current => ({ ...current, password: event.target.value }))} /></label>
        <button type="submit">{t('admin.login')}</button>
      </form>
    </section> : <section className="admin-users-results" aria-busy={loading}>
      <form className="admin-users-search" onSubmit={handleSearch}>
        <label>{t('admin.users.searchLabel')}<input type="search" maxLength={120} placeholder={t('admin.users.searchPlaceholder')} value={search} onChange={event => setSearch(event.target.value)} /></label>
        <button type="submit" disabled={loading}>{t('admin.users.search')}</button>
        <button type="button" disabled={loading} onClick={() => changeQuery({})}>{t('admin.refresh')}</button>
      </form>
      {loading && <p role="status">{t('admin.users.loading')}</p>}
      {data?.accessKey === accessKey && <>
        <p className="admin-users-count">{t('admin.users.total', { count: formatNumber(data.total) })}</p>
        {data.items.length ? <div className="admin-users-table">
          <table>
            <caption className="admin-users-caption">{t('admin.users.table')}</caption>
            <thead><tr>{['email', 'name', 'role', 'status', 'registeredAt'].map(column => <th scope="col" key={column}>{t(`admin.users.${column}`)}</th>)}</tr></thead>
            <tbody>{data.items.map(item => <tr key={item.id}>
              <td>{item.email || t('admin.notSet')}</td>
              <td>{item.name || t('admin.notSet')}</td>
              <td>{t(`admin.users.role.${ROLES.includes(item.role) ? item.role : 'unknown'}`)}</td>
              <td>{t(`admin.users.status.${STATUSES.includes(item.status) ? item.status : 'unknown'}`)}</td>
              <td>{registeredAt(item.created_at)}</td>
            </tr>)}</tbody>
          </table>
        </div> : <p className="admin-users-empty">{t('admin.users.empty')}</p>}
        <nav className="admin-users-pagination" aria-label={t('admin.users.pagination')}>
          <button type="button" disabled={loading || query.page <= 1} onClick={() => changeQuery({ page: query.page - 1 })}>{t('admin.users.previous')}</button>
          <span>{t('admin.users.page', { page: formatNumber(query.page), pages: formatNumber(pages) })}</span>
          <button type="button" disabled={loading || query.page >= pages} onClick={() => changeQuery({ page: query.page + 1 })}>{t('admin.users.next')}</button>
        </nav>
      </>}
    </section>}
  </main>;
}
